import { useQuery } from '@tanstack/react-query'
import apiClient from '@/api/client'
import { useCurrency } from '@/contexts/CurrencyContext'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { formatDateHuman } from '@/lib/utils'
import { PURCHASING_STRINGS } from './purchasingStrings'

const S = PURCHASING_STRINGS.pettyCash

/** Kitchen flow lane D: quick purchases paid from petty cash (last 30
 *  business days). They also appear in Expenses as cash inventory purchases. */
export function PettyCashDialog({ open, onClose, onOpenPo }: { open: boolean; onClose: () => void; onOpenPo: (id: string) => void }) {
  const { formatCurrency } = useCurrency()
  const { data, isFetching: isLoading } = useQuery({
    queryKey: ['pettyCash'],
    queryFn: () => apiClient.getPettyCashReport().then((r) => r.data),
    enabled: open,
    // Always fetch fresh when shown: the app-wide 5-minute staleTime would
    // otherwise show a list from before the last receipt / count / issue.
    staleTime: 0,
  })
  const rows = data?.purchases ?? []
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl">{S.title}</DialogTitle>
          <DialogDescription>{S.help}</DialogDescription>
        </DialogHeader>
        {isLoading ? (
          <p className="py-6 text-sm text-muted-foreground">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">{S.empty}</p>
        ) : (
          <>
            <ul className="divide-y rounded-lg border">
              {rows.map((r) => (
                <li key={r.purchase_order_id}>
                  <button type="button" className="flex min-h-10 w-full items-center justify-between gap-3 px-3 py-2 text-left hover:bg-muted/40"
                    onClick={() => { onClose(); onOpenPo(r.purchase_order_id) }}>
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-medium">{r.supplier_name}</span>
                      <span className="block text-xs text-muted-foreground">
                        {formatDateHuman(r.date)}{r.recorded_by ? ` · ${r.recorded_by}` : ''}{r.reference_no ? ` · ${r.reference_no}` : ''}
                      </span>
                    </span>
                    <span className="shrink-0 text-sm font-semibold tabular-nums">{formatCurrency(r.amount)}</span>
                  </button>
                </li>
              ))}
            </ul>
            <p className="text-right text-sm">
              {S.total} <span className="font-bold tabular-nums">{formatCurrency(data?.total ?? 0)}</span>
            </p>
          </>
        )}
        <DialogFooter>
          <Button onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
